import _ from "lodash";
import {
  DataLocation,
  KeyedDataLocationObject,
  TaskDataLocationObject,
} from "common/flowmaster/types/data_location";
import {
  DataStore,
  AsyncDataStoreParameterValues,
  DataStoreParameterValues,
} from "common/flowmaster/types/data_store";
import {ActionOutput} from "common/flowmaster/types/workflow";
import {WorkflowTaskId} from "common/flowmaster/types/task_config";

export default async function getValue<T>(
  location: DataLocation<T>,
  dataStore: DataStore,
  listItem: string | object | null = null,
): Promise<T> {
  if (location.source_type === "none") {
    return null as T;
  }
  if (location.source_type === "literal") {
    return location.value;
  }
  if (location.source_type === "listItem") {
    return location.value ? _.get(listItem, location.value) : listItem;
  }
  if (location.key === null || location.key.toString().length === 0) {
    throw new Error("Location key is empty");
  }

  const value: T = await determineValue(location, dataStore);
  return value;
}

async function determineValue<T>(
  location: DataLocation<T>,
  dataStore: DataStore,
): Promise<T> {
  const source = dataStore[location.source_type];
  switch (location.source_type) {
    case "prompt":
    case "context":
      return (await getAsyncDataLocation(
        location,
        source as AsyncDataStoreParameterValues<string>,
        location.source_type,
        null,
      )) as T;
    case "input": {
      return (await getAsyncDataLocation(
        location,
        source as AsyncDataStoreParameterValues<string>,
        location.source_type,
        location?.value ?? null,
      )) as T;
    }
    case "variable": {
      return (await getVariableDataLocation(
        location,
        source as AsyncDataStoreParameterValues<string>,
      )) as T;
    }
    case "task": {
      return (await getTaskDataLocation(
        location,
        source as DataStoreParameterValues<ActionOutput>,
      )) as T;
    }
    default:
      throw new Error("Source type does not match context/input/task/prompt");
  }
}

async function getAsyncDataLocation(
  location: KeyedDataLocationObject,
  source: AsyncDataStoreParameterValues<string>,
  source_type: string,
  accessor: string | null = null,
) {
  const value = await source[location.key];
  if (value === undefined) {
    throw new Error(`Key ${location.key} does not exist in ${source_type}`);
  }
  return getValueForKey(accessor, value);
}

async function getVariableDataLocation(
  location: KeyedDataLocationObject,
  source: AsyncDataStoreParameterValues<string>,
) {
  const value = source[location.key];
  if (value === undefined) {
    return "";
  }
  return value;
}

export async function getTaskDataLocation(
  location: TaskDataLocationObject,
  source: DataStoreParameterValues<ActionOutput>,
) {
  const taskId: WorkflowTaskId = location.task;
  const task = source?.[taskId];

  if (task === undefined || task === null) {
    throw new Error(`Task ${taskId} does not exist in the task data store.`);
  }
  const taskValue = task[location.key] as string | Record<string, unknown>;
  if (taskValue === undefined) {
    throw new Error(`Key "${location.key}" does not exist on task ${taskId}`);
  }
  return getValueForKey(location?.value, taskValue);
}
function getValueForKey(
  key: string | null,
  taskValue: string | Record<string, unknown> | null,
) {
  return typeof key === "string" &&
    key.length > 0 &&
    typeof taskValue === "object"
    ? _.get(taskValue, key)
    : taskValue;
}
